import { useEffect, useRef, useState } from 'react';
import {
  fetchMonitoringFarmersListForUser,
  formatFarmerFieldsLabel,
  formatFarmerMobileLabel,
  isRecordedContactValue,
  searchExplorerFarmers,
  type ExplorerFarmerOption,
} from '../../../config/digisakaApi';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';

type FarmerSearchPickerProps = {
  selected: ExplorerFarmerOption | null;
  onSelect: (farmer: ExplorerFarmerOption | null) => void;
  disabled?: boolean;
  /** When true, show a required asterisk on the label. */
  required?: boolean;
  error?: string;
  /** Admin searches all farmers; LGU searches farmers in the monitoring area. */
  source?: 'explorer' | 'monitoring';
  helperText?: string;
};

const SEARCH_DEBOUNCE_MS = 300;

function normalizeFarmerRow(row: ExplorerFarmerOption): ExplorerFarmerOption | null {
  const farmerId = Number(row?.farmer_id);
  if (!Number.isFinite(farmerId) || farmerId <= 0) return null;
  const name = String(row.name || row.label || `Farmer ${farmerId}`).trim();
  return {
    ...row,
    farmer_id: farmerId,
    name,
    label: String(row.label || name).trim() || name,
  };
}

export default function FarmerSearchPicker({
  selected,
  onSelect,
  disabled = false,
  required = false,
  error,
  source = 'explorer',
  helperText,
}: FarmerSearchPickerProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const inputClass = `${ap.input} rounded-md shadow-sm`;
  const labelClass = `block text-sm font-medium mb-1 ${ap.label}`;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExplorerFarmerOption[]>([]);
  const [catalog, setCatalog] = useState<ExplorerFarmerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (source !== 'monitoring') return;
    let cancelled = false;
    setLoading(true);
    void fetchMonitoringFarmersListForUser()
      .then((rows) => {
        if (cancelled) return;
        setCatalog(rows.map(normalizeFarmerRow).filter(Boolean) as ExplorerFarmerOption[]);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCatalog([]);
        setSearchError(err instanceof Error ? err.message : 'Could not load farmers.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (selected) {
      setResults([]);
      return;
    }

    if (source === 'monitoring') {
      const q = query.trim().toLowerCase();
      const filtered = catalog.filter((farmer) => {
        if (!q) return true;
        const hay = `${farmer.name} ${farmer.label} ${farmer.mobile || ''} ${farmer.farmer_id}`.toLowerCase();
        return hay.includes(q);
      });
      setResults(filtered.slice(0, 20));
      setSearchError('');
      return;
    }

    const q = query.trim();
    if (q.length < 1) {
      setResults([]);
      setLoading(false);
      setSearchError('');
      return;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setSearchError('');
    const timer = window.setTimeout(() => {
      void searchExplorerFarmers(q, 20)
        .then((rows) => {
          if (reqId !== requestIdRef.current) return;
          setResults(rows);
          setOpen(true);
        })
        .catch((err: unknown) => {
          if (reqId !== requestIdRef.current) return;
          setResults([]);
          setSearchError(err instanceof Error ? err.message : 'Farmer search failed.');
        })
        .finally(() => {
          if (reqId === requestIdRef.current) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, selected, source, catalog]);

  const clearSelection = () => {
    onSelect(null);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className={labelClass}>
        Assign farmer{required ? ' *' : ''}
      </label>

      {selected ? (
        <div
          className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 ${
            isDark ? 'border-emerald-700/50 bg-emerald-900/20' : 'border-emerald-200 bg-emerald-50'
          }`}
        >
          <div className="min-w-0">
            <p className={`truncate text-sm font-medium ${ap.heading}`}>
              {selected.name?.trim() ||
                (selected.label || '').replace(/\s*\(Farmer\s*#?\d+\)\s*/gi, '').trim() ||
                selected.label}
            </p>
            <p className={`truncate text-xs ${ap.faint}`}>
              {[
                `Farmer #${selected.farmer_id}`,
                formatFarmerMobileLabel(selected.mobile),
                formatFarmerFieldsLabel(selected.farms_count),
              ].join(' · ')}
            </p>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            disabled={disabled}
            className={`shrink-0 rounded p-1 disabled:opacity-50 ${
              isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-white'
            }`}
            aria-label="Clear selected farmer"
            title="Clear farmer"
          >
            <i className="ri-close-line text-base" aria-hidden />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <i
              className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                if (source === 'monitoring' || results.length > 0) setOpen(true);
              }}
              className={`${inputClass} pl-8 pr-8 ${
                error ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : ''
              }`}
              placeholder={
                source === 'monitoring'
                  ? 'Search a farmer in your monitoring area…'
                  : 'Search farmer by name…'
              }
              disabled={disabled}
              autoComplete="off"
              aria-label="Search farmer by name"
              aria-expanded={open}
              aria-controls="farmer-search-results"
            />
            {loading && (
              <span
                className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin rounded-full border border-current border-t-transparent text-gray-400"
                aria-hidden
              />
            )}
          </div>

          {open && (source === 'monitoring' || query.trim().length > 0) && !loading && (
            <ul
              id="farmer-search-results"
              role="listbox"
              className={`absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border shadow-lg ${
                isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              {results.length === 0 ? (
                <li className={`px-3 py-2 text-xs ${ap.faint}`}>
                  {searchError || 'No farmers found.'}
                </li>
              ) : (
                results.map((farmer) => (
                  <li key={farmer.farmer_id}>
                    <button
                      type="button"
                      role="option"
                      className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${
                        isDark ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-emerald-50 text-gray-800'
                      }`}
                      onClick={() => {
                        onSelect(farmer);
                        setQuery('');
                        setResults([]);
                        setOpen(false);
                      }}
                    >
                      <span className="font-medium">
                        {farmer.name?.trim() ||
                          (farmer.label || '').replace(/\s*\(Farmer\s*#?\d+\)\s*/gi, '').trim() ||
                          farmer.label}
                      </span>
                      <span className={`text-xs ${ap.faint}`}>
                        {[
                          formatFarmerMobileLabel(farmer.mobile),
                          isRecordedContactValue(farmer.email) ? String(farmer.email).trim() : null,
                          formatFarmerFieldsLabel(farmer.farms_count),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </>
      )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      {!error && !selected && (
        <p className={`mt-1 text-xs ${ap.faint}`}>
          {helperText ||
            (source === 'monitoring'
              ? 'Select a farmer from your monitoring area to link this field.'
              : 'Search and select a Digisaka farmer to link this field.')}
        </p>
      )}
    </div>
  );
}
